import _ from 'underscore';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  coerceStatus,
} from '@bingads-webui-campaign/overview-cards-common';
import { defaultMetrics } from '../common/metrics';
import ConversionTrackingGrid from './react-grid/react-grid';
import StatusOverview from './conversion-error-overview/status-overview';
import ConversionTrackingFooter from './conversion-error-overview/footer';
import GridHeader from './react-grid/grid-header';
import { GoalCategory } from '../common/strings';

function processGridData({
  metric,
  data,
  entitySet,
  deps,
  newI18n,
}) {
  const {
    metricFormatter,
    i18n,
    instrumentation,
    constants,
  } = deps;

  if (data === undefined || data === null) {
    return undefined;
  } else if (_.size(data) === 0) {
    return [];
  }

  _.each(data, (entity) => {
    if (!entity.PerformanceMetricsChange) {
      instrumentation.logMessage([constants.LOGCATEGORY.ERROR], constants.LOGLEVEL.INFO, 'conversion-tracking-card', JSON.stringify(entity));
    }
  });

  const maxChange = Math.abs(_.max(data, ({
    PerformanceMetricsChange: { [metric]: value },
  }) => Math.abs(value)).PerformanceMetricsChange[metric]);

  return _.map(data, (entity) => {
    const formattedPeriodMetrics = metricFormatter.formatMetric(
      metric,
      entity.PeriodPerformanceMetrics[metric],
      { currency: entity.Currency }
    );
    const formattedMetrics = metricFormatter.formatMetric(
      metric,
      entity.PerformanceMetrics[metric],
      { currency: entity.Currency }
    );
    const value = entity.PerformanceMetricsChange[metric];
    let declineBar = 0;
    let declineSpace = 100;
    let increaseBar = 0;
    let increaseSpace = 100;

    if (maxChange !== 0) {
      if (value < 0) {
        declineBar = Math.ceil(-(value * 100) / maxChange);
        declineSpace = 100 - declineBar;
      } else {
        increaseBar = Math.ceil((value * 100) / maxChange);
        increaseSpace = 100 - increaseBar;
      }
    }

    const typeName = entitySet.type.elementType.shortName;
    const i18nString = GoalCategory[entity.Id] || entity.Id;
    return {
      name: newI18n.getString(i18nString),
      category: entity.Id,
      status: coerceStatus(entity.Status, entity.AccountInactiveReasons),
      value,
      formattedValue: metricFormatter.formatMetricWithSign(
        metric,
        entity.PerformanceMetricsChange[metric],
        { currency: entity.Currency }
      ),
      formattedPercentageValue: metricFormatter
        .formatChangePercentageWithSign(entity.PerformanceMetricsChangePercentage[metric]),
      fromTo: i18n.getString('Dashboard_Cards_FromTo', { fromValue: formattedPeriodMetrics, toValue: formattedMetrics }),
      campaignType: entity.CampaignType,
      typeName,
      formattedMetrics,
      formattedPeriodMetrics,
      declineBar,
      declineSpace,
      increaseBar,
      increaseSpace,
      rawStatus: entity.Status,
    };
  });
}


function ConversionTrackingState(props) {
  const {
    metric,
    categoryResults,
    statusSummaries,
    noConversionGoalsWithRevenue,
    dateRange,

    updateCurrentMenuItems,
    updateMetric,

    deps,
    entitySet,
    i18n,
    newI18n,
    permissions,
    instrumentationLog,
    TrackingTypeFilterStore,
    GoalCategoryFilterStore,
  } = props;

  const processedResults = useMemo(() => processGridData({
    deps,
    metric,
    data: categoryResults,
    entitySet,
    newI18n,
  }), [deps, metric, categoryResults, entitySet, newI18n]);

  const totalPageCount = useMemo(() => {
    if (_.size(statusSummaries) === 0) {
      return 1;
    }
    return 2;
  }, [statusSummaries]);

  const [pageNumber, setPageNumber] = useState(totalPageCount === 1 ? 1 : 0);

  useEffect(() => {
    if (pageNumber === 0) {
      updateCurrentMenuItems(false);
    } else {
      updateCurrentMenuItems(true);
    }
  }, [pageNumber, updateCurrentMenuItems]);

  const renderContent = useCallback(() => {
    if (pageNumber === 0) {
      return (
        <StatusOverview
          data={statusSummaries}
          newI18n={newI18n}
          noConversionGoalsWithRevenue={noConversionGoalsWithRevenue}
          TrackingTypeFilterStore={TrackingTypeFilterStore}
          router={deps.legacyRouter}
          instrumentationLog={instrumentationLog}
        />
      );
    }
    return (
      <React.Fragment>
        <GridHeader
          metrics={defaultMetrics}
          dateRange={dateRange}
          newI18n={newI18n}
          i18n={i18n}
          updateMetric={updateMetric}
          permissions={permissions}
        />
        <ConversionTrackingGrid
          processedData={processedResults}
          metric={metric}
          deps={deps}
          newI18n={newI18n}
          i18n={i18n}
          instrumentationLog={instrumentationLog}
          GoalCategoryFilterStore={GoalCategoryFilterStore}
          router={deps.legacyRouter}
          dateRange={dateRange}
        />
      </React.Fragment>
    );
  }, [GoalCategoryFilterStore, TrackingTypeFilterStore, dateRange, deps, i18n, instrumentationLog, metric, newI18n, noConversionGoalsWithRevenue, pageNumber, permissions, processedResults, statusSummaries, updateMetric]);

  return (
    <div className="conversion-tracking-state">
      {renderContent()}
      <ConversionTrackingFooter
        newI18n={newI18n}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        permissions={permissions}
        router={deps.legacyRouter}
        instrumentationLog={instrumentationLog}
        totalPageCount={totalPageCount}
      />
    </div>
  );
}

ConversionTrackingState.defaultProps = {
  updateCurrentMenuItems: _.noop,
  instrumentationLog: {
    SetUpConversionTracking: _.noop,
    CouponIsShown: _.noop,
    NoCoupon: _.noop,
    ManageConversionsClick: _.noop,
    SeeAllClicked: _.noop,
    Error: _.noop,
  },

  updateMetric: _.noop,
  categoryResults: [],
  statusSummaries: [],
  noConversionGoalsWithRevenue: false,
  dateRange: {},
  TrackingTypeFilterStore: _.noop,
  GoalCategoryFilterStore: _.noop,

};
ConversionTrackingState.propTypes = {
  metric: PropTypes.string.isRequired,
  categoryResults: PropTypes.arrayOf(PropTypes.shape({
  })),
  statusSummaries: PropTypes.arrayOf(PropTypes.shape({
    GoalCount: PropTypes.number,
    GoalNames: PropTypes.arrayOf(PropTypes.string),
    GoalTrackingStatus: PropTypes.string,
  })),
  noConversionGoalsWithRevenue: PropTypes.bool,

  dateRange: PropTypes.shape({
    RangeStartDate: PropTypes.string.isRequired,
    RangeEndDate: PropTypes.string.isRequired,
  }),
  updateCurrentMenuItems: PropTypes.func,

  deps: PropTypes.shape({
    metricFormatter: PropTypes.shape({
      formatMetric: PropTypes.func.isRequired,
      formatMetricWithSign: PropTypes.func.isRequired,
      formatChangePercentageWithSign: PropTypes.func.isRequired,
    }).isRequired,
    legacyRouter: PropTypes.shape({
      navigateToRoute: PropTypes.func.isRequired,
    }).isRequired,
    pageContext: PropTypes.shape({
      accountId: PropTypes.string,
      adgroupId: PropTypes.string,
      campaignId: PropTypes.string,
    }).isRequired,
  }).isRequired,


  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,

  instrumentationLog: PropTypes.shape({
    SetUpConversionTracking: PropTypes.func,
    CouponIsShown: PropTypes.func,
    NoCoupon: PropTypes.func,
    ManageConversionsClick: PropTypes.func,
    SeeAllClicked: PropTypes.func,
    Error: PropTypes.func,
  }),

  updateMetric: PropTypes.func,
  TrackingTypeFilterStore: PropTypes.func,
  GoalCategoryFilterStore: PropTypes.func,
  entitySet: PropTypes.shape({}).isRequired,
};

export default ConversionTrackingState;
